"use client";

import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import {
    Mail, ShieldCheck, Activity, Users,
    FileText, BarChart3
} from "lucide-react";

const features = [
    {
        icon: Mail,
        title: "Campaign Builder",
        description: "Create, schedule, and send email campaigns with custom templates, recipient lists, IP warmup, and configurable threading.",
        color: "from-indigo-500 to-blue-600",
        size: "col-span-1 row-span-1 lg:col-span-2",
    },
    {
        icon: ShieldCheck,
        title: "Email Verification",
        description: "Deep verification with syntax, MX, SMTP, disposable detection, and risk scoring — before you send.",
        color: "from-emerald-500 to-teal-600",
        size: "col-span-1 row-span-1",
    },
    {
        icon: Activity,
        title: "Real-Time Tracking",
        description: "Live WebSocket-powered dashboard with animated progress, activity feed, and per-email status updates.",
        color: "from-rose-500 to-orange-500",
        size: "col-span-1 row-span-1",
    },
    {
        icon: Users,
        title: "Contact CRM",
        description: "Full contact management with bulk CSV/Excel import, custom statuses, sources, locations, and smart list segmentation.",
        color: "from-violet-500 to-purple-600",
        size: "col-span-1 row-span-1",
    },
    {
        icon: FileText,
        title: "Template Engine",
        description: "HTML email editor with live preview and test sending capabilities.",
        color: "from-amber-500 to-orange-500",
        size: "col-span-1 row-span-1",
    },
    {
        icon: BarChart3,
        title: "Full Analytics",
        description: "Track deliveries, opens, clicks, bounces, spam reports, and unsubscribes across all campaigns with actionable insights.",
        color: "from-cyan-500 to-blue-600",
        size: "col-span-1 row-span-1 lg:col-span-2",
    },
];

function FeatureCard({ feature, index }: { feature: any, index: number }) {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });
    
    // 3D Tilt Values
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseXSpring = useSpring(x);
    const mouseYSpring = useSpring(y);

    const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
    const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

    // Spotlight cursor effect
    const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const xPct = mouseX / width - 0.5;
        const yPct = mouseY / height - 0.5;

        x.set(xPct);
        y.set(yPct);
        setCursorPos({ x: mouseX, y: mouseY });
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ 
                duration: 0.8, 
                delay: index * 0.1,
                ease: [0.21, 0.47, 0.32, 0.98] 
            }}
            style={{
                rotateX,
                rotateY,
                transformStyle: "preserve-3d",
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={`${feature.size} group relative p-8 rounded-[2rem] bg-white/70 dark:bg-white/[0.03] backdrop-blur-2xl border border-gray-100 dark:border-white/[0.08] shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-default`}
        >
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none">
                <div className="absolute inset-0 bg-[radial-gradient(#6366f1_1px,transparent_1px)] bg-[size:20px_20px]" />
            </div>

            {/* Spotlight Glow */}
            <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `radial-gradient(600px circle at ${cursorPos.x}px ${cursorPos.y}px, rgba(99, 102, 241, 0.08), transparent 40%)`
                }}
            />

            {/* Icon - Floating & Styled */}
            <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: index * 0.5 }}
                className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 relative z-10`}
            >
                <feature.icon className="h-7 w-7 text-white" />
                <div className="absolute inset-0 rounded-2xl bg-white/20 blur-xl opacity-0 group-hover:opacity-50 transition-opacity" />
            </motion.div>

            {/* Text Content */}
            <div className="relative z-10" style={{ transform: "translateZ(30px)" }}>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                    {feature.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed font-medium">
                    {feature.description}
                </p>
            </div>

            {/* Bottom Glow Accent */}
            <div className={`absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br ${feature.color} opacity-[0.03] group-hover:opacity-[0.1] blur-3xl transition-opacity duration-700`} />
            
            {/* Hover Shine Effect */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 pointer-events-none bg-gradient-to-tr from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
        </motion.div>
    );
}

export default function FeaturesGrid() {
    const sectionRef = useRef<HTMLDivElement>(null);
    const sectionInView = useInView(sectionRef, { once: true, margin: "-100px" });

    return (
        <section ref={sectionRef} className="relative py-24 px-6 overflow-hidden">
            {/* Decorative background beam */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-[500px] bg-indigo-500/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="relative max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={sectionInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="text-center mb-20"
                >
                    <motion.span 
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={sectionInView ? { opacity: 1, scale: 1 } : {}}
                        transition={{ delay: 0.2 }}
                        className="inline-flex items-center px-4 py-1.5 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-xs font-black uppercase tracking-[0.2em] mb-6 border border-indigo-500/20 shadow-sm"
                    >
                        Capabilities
                    </motion.span>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white tracking-tight leading-[1.1]">
                        The Full Power of{" "}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 dark:from-indigo-400 dark:via-purple-400 dark:to-violet-400">
                            iComm Platforms
                        </span>
                    </h2>
                    <p className="mt-6 text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-medium">
                        Everything you need to verify, send, and track campaigns that convert.
                    </p>
                </motion.div>

                {/* Bento Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 perspective-1000">
                    {features.map((feature, i) => (
                        <FeatureCard key={feature.title} feature={feature} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
